import { HttpClient } from "../http-client";
import { EntityDocumentsEnum } from "entities/onboard/model/onboardModel";
import { EntityType } from "static/types";
import { IUser, sessionStore } from "entities/session/model/sessionModel";
import { IFavoritesDto, IFilterDto } from "entities/market";
import { KYB_Step, KYC_Status, KYC_Step } from "entities/account";

export interface LoginUserDto {
  entity: any;
  email: string;
  phone: string;
  password: string;
}

export interface LogoutDto {
  platform: string;
}

export interface RegisterDto {
  email: string;
  firstName: string;
  lastName: string;
  companyName: string;
  password: string;
  subscribe: boolean;
}

export interface RegisterPhoneDto {
  phone: string;
  entity: any;
  language: string;
  recaptchaToken: string | null;
  registerFrom: string | null;
}

export interface ForgotPasswordDto {
  phone: string;
  entity: any;
  language: string;
}

export interface VerifyPhoneDto {
  id: number;
  entity: any;
  code: string;
}

export interface RegisterEmailDto {
  entity: any;
  email: string;
  language: string;
}

export interface VerifyEmailDto {
  entity: any;
  code: string;
}

export interface RestorePasswordDto {
  id: number;
  entity: any;
  code: string;
  password: string;
}

export interface BiometricAuthDto {
  entity: any;
  email: string;
}

export interface BiometricAuthVerifyDto {
  entity: any;
  email: string;
  response: any;
  platform: string;
  pinCode: string;
}

export interface BiometricLoginDto {
  identificator: string;
}

export interface BiometricVerifyDto {
  platform: string;
  biometric_id: string;
  biometric_response: any;
}

export interface BiometricLoginVerifyDto extends BiometricVerifyDto {
  identificator: string;
}

export interface RegisterNameDto {
  entity: any;
  firstName?: string;
  lastName?: string;
  companyName?: string;
}

export interface RegisterPasswordDto {
  entity: any;
  password: string;
}

export interface RegisterInfoDto {
  profileInfo: any;
}

export interface RegisterAddressDto {
  address: any;
}

export interface CheckAgreeDto {
  entity: any;
  subscribe: boolean;
}

export interface PinCodeDto {
  code: string;
}

export interface UserDto {
  code: number;
  data: {
    user: IUser;
    entity: EntityType;
    accessToken: string;
    refreshToken: string;
    kyc: any;
    kyb: any;
    questionnaireStatus: KYC_Status;
    biometrics: any[];
    biometric: string | null;
    fcmToken: string | null;
    crsData: any[];
    subAccounts: any[];
  };
}

export interface SendDocumentDto {
  country: string;
  citizenship?: string;
  idDocType:
    | "ID_CARD"
    | "PASSPORT"
    | "SELFIE"
    | "UTILITY_BILL"
    | "RESIDENCE_PERMIT"
    | "COMPANY_DOC"
    | "INTERNAL_PASSPORT";
  idDocSubType?: "FRONT_SIDE" | "BACK_SIDE";
  fiz: File;
  document_info?: {
    document_number: string;
    first_name: string;
    last_name: string;
    original_first_name: string;
    original_last_name: string;
    birth_date: string;
    gender: string;
  };
}
export interface CheckDocumentDto {
  type: "QUALITY" | "LIVENESS" | "PARSING";
  fiz: File;
}

export interface CheckDocumentDataDto {
  data: {
    result: "SUCCESS" | "FAILED";
    data: {
      score: number;
      info: {
        first_name: string;
        last_name: string;
        original_first_name: string;
        original_last_name: string;
        birth_date: string; // YYYY-MM-DD
        gender: string; // MALE | FEMALE
        document_number: string;
      };
    };
  };
}
export interface DeleteDocumentDto {
  country: string;
}

export interface UploadAvatarDto {
  avatar: File;
}

export interface SignContractDto {
  pinCode: string;
}

export interface ChangePasswordDto {
  password: string;
  newPassword: string;
}

export interface KycStartDto {}

export interface SubAccountDto {
  name: string;
}

type StepKYCDtoType =
  | {
      step: KYC_Step.Residence;
      residence: string;
      nationality: string;
      place_of_birth: string;
    }
  | {
      step: KYC_Step.Name;
      first_name: string;
      last_name: string;
    }
  | {
      step: KYC_Step.DateOfBirth;
      birth_date: string;
    }
  | {
      step: KYC_Step.EmailVerification;
      email: string;
      code: string;
    }
  | {
      step: KYC_Step.PhoneVerification;
      phone: string;
      code: string;
    }
  | {
      step: KYC_Step.Address;
      city: string;
      street: string;
      building: string;
      apartment: string;
      zip_code: string;
    }
  // | {
  //     step: KYC_Step.DocumentNumber;
  //     document_number: string;
  //     is_political_figure: boolean;
  //   }
  | {
      step: KYB_Step.Name;
      company_name: string;
    }
  | {
      step: KYB_Step.Documents;
      company_address: string;
    };

export type KycStepsDto = any & {
  step: number; // remove
  dto: StepKYCDtoType;
};

export type KycInfoDto = {
  address: {
    city: string;
    street: string;
    building: string;
    apartment: string;
    zip_code: string;
  };
};

export interface AssignmentDto {
  type: string;
  dto: any;
}

export interface ChangePinCodeDto {
  pinCode: string;
  newPinCode: string;
}
export interface ResetPinCodeDto {
  password: string;
}

export interface SendEntityDocumentDto {
  type: EntityDocumentsEnum | string;
  index: string;
  entity: Blob;
}

interface ResponseDto {
  code: number;
  data: {
    user: {
      id: number;
      phone: string;
      email: string;
      profileInfo: any;
      personalAddress: string;
      documents: any;
      additionalDocument: any;
    };
    entity: EntityType;
    accessToken: string;
    refreshToken: string;
    kyc: any;
    kyb: any;
    questionnaireStatus: KYC_Status;
    biometrics: any[];
    subAccounts: any[];
  };
}

interface RegisterFirstStepDto {
  entity: string | null;
  phone: string;
  password: string;
  recaptchaToken: string | null;
  language: string;
  registerFrom: string;
}

interface RegisterSecondStepDto {
  entity: string;
  phone: string;
  password: string;
  code: string;
}

export interface WithdrawRequestDto {
  currency: string;
  account_number: string;
  amount: number;
  info_source: number | null;
  data?: Record<string, string>;
  /** a bank that is not on our list: every field becomes required */
  bank_type?: WithdrawalBankType;
}
export type WithdrawalBankType = "russia" | "foreign";
/** What the client last typed in for this kind of bank and currency. */
export interface IWithdrawalRequisites {
  bank_type: WithdrawalBankType;
  currency: string;
  account_number: string;
  data: Record<string, string>;
  updatedAt: string;
}
export interface DepositRequestDto {
  currency: string;
  amount: number;
  info_source: number | null;
  note?: string;
}
export interface WithdrawCancelDto {
  id: number;
}

export interface CalculateBondPriceDto {
  isin: string;
  price: null | number;
  yld: null | number;
}

export interface OpenOrderDto {
  pinCode: string;
  comments: string;
  asset: number;
  type: string;
  price: number;
  quantity: number;
  operation: string;
  with_sirius: boolean;
  use_capital_protection?: boolean;
  custom_commission?: number;
  tif?: string;
  specific_amount?: number;
  specific_currency?: string;
  option_contract_id?: number;
}

export interface EnableNotificationsDto {
  notification_token: string;
  platform: string;
}

export interface DisableNotificationsDto {
  platform: string;
}

export interface GenerateReportDto {
  from: string;
  to: string;
}

export interface CRSDataItemDto {
  country: string;
  tin: string;
  reason: string;
}

export interface CRSDataDto {
  year: number;
  data: Array<CRSDataItemDto>;
}

// export const GetTariffs = async () => {
//   try {
//     const resp = await instance.get("tariffs");
//     return { data: resp.data.data };
//   } catch (error) {
//     return { data: [] };
//   }
// };

export class Api<
  SecurityDataType extends unknown
> extends HttpClient<SecurityDataType> {
  auth = {
    token: this.getRequest<UserDto>("/auth/token"),
    login: this.postRequest<LoginUserDto, UserDto>("/auth/login"),
    logout: this.postRequest<LogoutDto>("/auth/logout"),
    forgot: this.postRequest<ForgotPasswordDto>("/auth/restore-password"),
    codeVerify: this.postRequest<RestorePasswordDto, UserDto>("/auth/confirm"),
    biometricAuth: this.postRequest<BiometricAuthDto>(
      "/auth/register-biometrics-options"
    ),
    biometricAuthVerify: this.postRequest<BiometricAuthVerifyDto>(
      "/auth/register-biometrics"
    ),
    loginByBiometric: this.postRequest<BiometricLoginDto>(
      "/auth/login-biometrics-options"
    ),
    loginByBiometricVerify: this.postRequest<BiometricLoginVerifyDto, UserDto>(
      "/auth/login-biometrics"
    ),
    biometricsOptions: this.postRequest("/auth/biometrics-options"),
    removeBiometrics: this.postRequest<BiometricVerifyDto>(
      "/auth/remove-biometrics"
    ),
    crsData: this.postRequest<CRSDataDto>("/auth/crs-data"),
  };
  onboarding = {
    recaptcha: this.postRequest("/auth/verify-recaptcha"),

    verificationLoad: this.getRequest("/auth/verification/load"),
    regPhone: this.postRequest<RegisterPhoneDto>("/auth/reg/set-phone"),
    verifyPhone: this.postRequest<VerifyPhoneDto, ResponseDto>(
      "/auth/reg/verify-phone"
    ),
    register: this.postRequest<RegisterDto, ResponseDto>("/auth/register/form"),
    registerFirstStep: this.postRequest<RegisterFirstStepDto>(
      "/auth/register-v2/first-step"
    ),
    registerSecondStep: this.postRequest<RegisterSecondStepDto>(
      "/auth/register-v2/second-step"
    ),
    regEmail: this.postRequest<RegisterEmailDto, ResponseDto>(
      "/auth/reg/set-email"
    ),
    verifyEmail: this.postRequest<VerifyEmailDto, ResponseDto>(
      "/auth/reg/verify-email"
    ),
    regName: this.postRequest<RegisterNameDto, ResponseDto>(
      "/auth/reg/set-name"
    ),
    regPassword: this.postRequest<RegisterPasswordDto, ResponseDto>(
      "/auth/reg/set-password"
    ),
    profileInfo: this.postRequest<RegisterInfoDto, ResponseDto>(
      "/auth/reg/set-info"
    ),
    document: this.postRequest<SendDocumentDto>(
      "/auth/verification/send-document"
    ),
    checkDocument: this.postRequest<CheckDocumentDto>(
      "/auth/verification/check-document"
    ),
    deleteDocument: this.deleteRequest<DeleteDocumentDto>(
      "/auth/verification/delete-document"
    ),
    entityDocument: this.postRequest<SendEntityDocumentDto>(
      "/auth/verification/send-entity-doc"
    ),
    deleteEntityDocument: this.deleteRequest<SendEntityDocumentDto>(
      "/auth/verification/delete-entity-doc"
    ),
    entityAdditionalDocument: this.postRequest<
      SendEntityDocumentDto,
      ResponseDto
    >("/auth/verification/send-entity-add-doc"),
    sign: this.postRequest<any>("/auth/verification/sign-contract"),
    regAddress: this.postRequest<RegisterAddressDto, ResponseDto>(
      "/auth/reg/set-address"
    ),
    checkAgree: this.postRequest<CheckAgreeDto, ResponseDto>(
      "/auth/reg/check-agree"
    ),
    pinCode: this.postRequest<PinCodeDto, ResponseDto>(
      "/auth/reg/set-pin-code"
    ),
  };
  dashboard = {
    currencies: this.getRequest("/dashboard/currencies"),
    exchanges: this.getRequest("/dashboard/exchanges"),
    quotes: this.getRequest("/dashboard/quotes"),
    bondsQuotes: this.getRequest("/dashboard/quotes/bonds"),
    brokerageAssets: this.getRequest("/dashboard/brokerage-assets"),
    rates: this.getRequest("/dashboard/rates"),
    accounts: this.getRequest("/dashboard/accounts"),
    stories: this.getRequest("/story/all"),
  };
  account = {
    uploadAvatar: this.postRequest<UploadAvatarDto>("/account/avatar"),
    getContract: this.getRequest("/account/sign-contract"),
    signContract: this.postRequest<SignContractDto>(
      "/account/sign-contract",
      true
    ),
    previewIccContract: this.getRequest(`/account/preview-agent-contract`),
    getDocuments: this.getRequest("/account/documents"),
    signIccContract: this.postRequest("/account/agent-contract", true),
    removeAvatar: this.deleteRequest("/account/avatar"),
    changePass: this.postRequest<ChangePasswordDto>("/account/change-password"),
    changePinCode: this.postRequest<ChangePinCodeDto>(
      "/account/change-pin-code"
    ),
    resetPinCode: this.postRequest<ResetPinCodeDto>("/account/reset-pin-code"),

    requestChangeEmail: this.postRequest<Partial<RegisterEmailDto>>(
      "/account/request-change-email"
    ),
    confirmChangeEmail: this.postRequest<{ code: string }>(
      "/account/confirm-change-email"
    ),
    requestChangePhone: this.postRequest<Partial<RegisterPhoneDto>>(
      "/account/request-change-phone"
    ),
    confirmChangePhone: this.postRequest<{ code: string }>(
      "/account/confirm-change-phone"
    ),
    activities: this.getRequest("/account/activities"),
    transactions: this.getRequest("/account/transactions"),
    activeTransactions: this.getRequest("/account/active-transactions"),
    currencyActivities: this.getRequest("/account/currency-activities"),
    reportsList: this.getRequest("/reports/list"),
    generateReport: this.postRequest<GenerateReportDto>("/reports/generate"),

    promoCode: (value: string) => this.postRequest(`/promo/${value}/activate`),
    requestDataDeletion: this.postRequest(
      `/account/request-data-deletion`,
      true
    ),

    startKyc: this.postRequest<KycStartDto>("/auth/verification/start-kyc"),
    stepKyc: this.postRequest<KycStepsDto>("/auth/verification/step-kyc"),
    infoKyc: this.postRequest<KycInfoDto>("/auth/verification/info-kyc"),
    startKyb: this.postRequest("/auth/verification/start-kyb"),
    stepKyb: this.postRequest<KycStepsDto>("/auth/verification/step-kyb"),

    questionLoad: this.getRequest(`/auth/questionnaire/load`),
    questionSubmit: this.postRequest(`/auth/questionnaire/submit`),

    bonusInfo: this.getRequest("/bonus/info"),
    bonusActivate: (value: number) =>
      this.postRequest(`/bonus/${value}/activate`),

    assignments: this.getRequest("/assignments"),
    getAssignment: (type: string) => this.getRequest(`/assignments/${type}`),
    assignmentSubmit: this.postRequest<AssignmentDto>("/assignments"),
    assignmentsPending: this.getRequest("/assignments/recent"),
    getAssignmentsHistory: this.getRequest("/assignments/history"),
    assignmentContract: (id: string) =>
      this.getRequest(`/assignments/${id}/request`),
    assignmentContractSigned: (id: number) =>
      this.postRequest(`/assignments/${id}/sign`, true),
    assignmentContractCanceled: (id: number) =>
      this.postRequest(`/assignments/${id}/cancel`, true),

    depositPayments: this.getRequest("/payments/deposit-payments"),
    repoDeals: this.getRequest("/repo/user"),

    analyticsMetadata: this.getRequest("/account/analytics-metadata"),
    firebaseTrigger: this.postRequest<{ type: string }>(
      `/account/firebase-trigger`
    ),

    subAccounts: this.getRequest("/account/sub-accounts"),
    createSubAccount: this.postRequest<SubAccountDto>("/account/sub-accounts"),
    updateSubAccount: (id: number) =>
      this.putRequest<SubAccountDto>(`/account/sub-accounts/${id}`),
    deleteSubAccount: (id: number) =>
      this.deleteRequest(`/account/sub-accounts/${id}`),
  };
  transfer = {
    depositInfo: this.getRequest("/transfer/deposit-info"),
    withdrawInfo: this.getRequest("/transfer/withdrawal-info"),
    withdrawRequisites: this.getRequest("/transfer/withdrawal-requisites"),
    withdrawRequest: this.postRequest<WithdrawRequestDto>(
      "/transfer/withdrawal-request"
    ),
    depositRequest: this.postRequest<DepositRequestDto>(
      "/transfer/deposit-request"
    ),
    withdrawCancel: this.postRequest<WithdrawCancelDto>(
      "/transfer/withdrawal-cancel"
    ),
    currencies: this.getRequest("/transfer/currencies"),
  };
  market = {
    assets: this.getRequest(`/market/assets`),
    assetsAPI: (type: number) =>
      this.getRequest(`/market/assets/${type}/symbols`),
    strategies: this.getRequest(`/market/assets/strategies-for-client`),
    getStrategy: (symbol: string) =>
      this.getRequest(`/market/assets/strategies/${symbol}`),
    whatToBuy: this.getRequest(`/what-to-buy/all`),
    assetPrice: (symbol: string) =>
      this.getRequest(`/market/assets/${symbol}/real-time`),
    search: (q: string) => this.postRequest(`/market/assets/search`)({ q }),
    assetInfo: (asset_id: number) =>
      this.getRequest(`/market/assets/${asset_id}/info`),
    assetDepth: (asset_id: number) =>
      this.getRequest(`/market/assets/${asset_id}/depth`),
    // assetChart: (asset_id: number) =>
    //   this.getRequest(`/market/assets/${asset_id}/chart`),
    assetChart: (asset_id: number) =>
      this.getRequest(`/market/assets/${asset_id}/chart-v2`),
    assetIcon: (type: number, symbol: string) =>
      this.getRequest(`/market/assets/${type}/${symbol}/icon`),
    calculateBondPrice: this.postRequest<CalculateBondPriceDto>(
      "/market/bonds/calculate-price"
    ),

    assetDeals: (id: number) => this.getRequest(`/market/assets/${id}/deals`),
    cancelDeal: (id: number) =>
      this.deleteRequest(`/market/assets/${id}/deals`),
    createDeal: (id: number) =>
      this.postRequest<OpenOrderDto>(`/market/assets/${id}/deals`, true),

    reviewDeal: (id: number) =>
      this.postRequest<OpenOrderDto>(`/market/assets/${id}/deals-review`, true),

    optionChain: (asset_id: number) =>
      this.getRequest(`/market/assets/${asset_id}/option-chain`),
    optionChainByBase: (asset_id: number) =>
      this.getRequest(`/market/assets/${asset_id}/option-chain-by-base`),

    getSpecialOffers: this.getRequest(`/fx/special-offers/user`),

    loadFilters: this.getRequest(`/filters/all`),
    createFilter: this.postRequest<IFilterDto>(`/filters/create`),
    updateFilter: (id: number) =>
      this.postRequest<IFilterDto>(`/filters/${id}/update`),
    deleteFilter: (id: number) => this.deleteRequest(`/filters/${id}/delete`),

    filterStockIndustries: this.getRequest(`/filters/industries`),
    filterSectors: this.getRequest(`/filters/sectors`),
    filterCountries: this.getRequest(`/filters/countries`),
    filterStockOptions: this.getRequest(`/filters/stock-filter-options`),

    getAssetsByFilters: this.postRequest(`/filters/assets-by-filter`),
    getBondsByFilters: this.postRequest(`/filters/bonds-by-filter`),
  };
  whatToBuy = {
    loadFavorites: this.getRequest(`/favorites`),
    addFavorites: this.postRequest<IFavoritesDto>(`/favorites`),
    removeFavorite: (id: number) => this.deleteRequest(`/favorites/${id}`),

    loadResearch: this.getRequest(`/research`),
    loadResearchByAlias: (alias: string) =>
      this.getRequest(`/research/${alias}`),
    loadResearchCategories: this.getRequest(`/research/category`),

    loadBlog: this.getRequest(`/blog`),
    loadBlogByAlias: (alias: string) => this.getRequest(`/blog/${alias}`),
    loadBlogCategories: this.getRequest(`/blog/category`),

    loadMembers: this.getRequest(`/members`),
  };
  chat = {
    check: this.getRequest(`/chat/check`),
  };
  nots = {
    enable: this.postRequest<EnableNotificationsDto>(
      "/notifications/push/enable"
    ),
    disable: this.postRequest<DisableNotificationsDto>(
      "/notifications/push/disable"
    ),
  };
  tariffsV2 = this.getRequest("/tariffs-v2");
  appInfo = this.getRequest("/app_info");
  handleError = async (dto: { error: string; error_info: string }) => {
    try {
      await this.postRequest("/handle-error")({
        platform: "web",
        error: dto.error,
        error_info: dto.error_info,
      });
    } catch (error) {}
  };
  changeLanguage = async (language: string) => {
    try {
      const user = sessionStore.getState().user;
      if (user) {
        sessionStore.setState({ user: { ...user, language } });
        await this.postRequest("/account/change-language")({
          language,
        });
      }
    } catch (error) {}
  };
}
